// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { FreebetsIcon } from '../../assets/img/betting';
import Formatter from '../../i18n/formatter';
import { __, getCurrency } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import {
  type BetslipType,
  BetslipTypes,
  type BetslipStatusType,
  BetslipStatus as BetslipStatusTypes,
} from '../../types/history';

const Wrapper = styled.div`
  margin: 0.67em 0;
  color: ${theme.History.betslip.summary.color};
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.67em 0 1em;
`;

const RowLabel = styled.span`
  flex: 1;
  display: inline-block;
  font-size: 0.87em;

  ${({ $uppercase }) => css`
    text-transform: ${$uppercase ? 'uppercase' : undefined};
  `};
`;

const RowValue = styled.span`
  font-size: 1em;
  font-weight: bold;
  border-radius: 6px;
  padding: 0.14em 0.33em;

  ${({ $color, $backgroundColor }) => css`
    color: ${$color ? $color : undefined};

    ${$backgroundColor
      ? css`
          background-color: ${$backgroundColor};
        `
      : css`
          font-size: 1.07em;
        `}
  `}
`;

const FreeBetsImg = styled.img`
  height: 1.2em;
  width: 1.2em;
  vertical-align: middle;
  margin-left: 0.3em;
`;

const RunningSummaryWrapper = styled.div``;

const DetailsRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0.3em 1em;
  padding-top: 0;
  color: ${theme.History.betslip.summary.detailsColor};
  font-size: 0.9em;
`;

const RowValueDetails = styled.span`
  display: inline-block;
  text-align: right;
  font-weight: bold;

  color: ${theme.History.betslip.summary.detailsColor};
  font-weight: bold;
`;

const LineSeparator = styled.div`
  height: 1px;
  margin: 0.33em 1em;

  background-color: ${theme.History.betslip.summary.separatorColor};
`;

const CompactSummary = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.67em 1em;
`;

const CompactSummaryContent = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.33em;
`;

const CompactSummaryLabel = styled.span`
  display: flex;

  color: ${theme.History.betslip.summary.compact.labelColor};
  font-size: 0.87em;
`;

const CompactSummaryValue = styled.div`
  font-size: 1.07em;
  font-weight: bold;

  ${({ $color }) => css`
    color: ${$color ?? theme.History.betslip.summary.compact.valueColor};
  `}
`;

type HistoryItemBetslipSummaryProps = {
  className?: string;
  isCompact?: boolean;
  type: BetslipType;
  globalOdds: number;
  details: Array<unknown>;
  freebet: boolean;
  betAmount: number;
  status: BetslipStatusType;
  winningsAmount: number;
  winningsBonus: number;
  promotionTitle: string;
  possibleBonus: number;
  possibleWinnings: number;
};

const HistoryItemBetslipSummary = ({
  className,
  isCompact,
  type,
  globalOdds,
  details,
  freebet,
  betAmount,
  status,
  winningsAmount,
  winningsBonus,
  promotionTitle,
  possibleBonus,
  possibleWinnings,
}: HistoryItemBetslipSummaryProps) => {
  const defaultSummary = useMemo(() => {
    return (
      <>
        {type === BetslipTypes.MULTIPLE ? (
          <SummaryRow>
            <RowLabel $color={theme.History.betslip.summary.totalOddsColor}>{__('Total odds')}</RowLabel>
            <RowValue $backgroundColor={theme.History.outcome.content.oddBackgroundColor}>
              {globalOdds
                ? Formatter.formatOdd(globalOdds)
                : Formatter.formatOdd(+details.reduce((prev, curr) => prev * curr.odds, 1))}
            </RowValue>
          </SummaryRow>
        ) : null}
        <SummaryRow>
          <RowLabel>
            {freebet ? (
              <>
                {__('Freebet Wager')}
                <FreeBetsImg src={FreebetsIcon} />
              </>
            ) : (
              __('Wager')
            )}
          </RowLabel>
          <RowValue>{Formatter.formatNumber(betAmount, getCurrency())}</RowValue>
        </SummaryRow>
      </>
    );
  }, [freebet, betAmount, type, globalOdds, details]);

  const settledAndCashoutSummary = useMemo(() => {
    if (status === BetslipStatusTypes.SETTLED || status === BetslipStatusTypes.CASHOUT) {
      const amount = winningsAmount + (winningsBonus || 0);
      return (
        <>
          {winningsBonus > 0 ? (
            <SummaryRow>
              <RowLabel>
                {__('Winnings')} {promotionTitle}
              </RowLabel>
              <RowValue>{Formatter.formatNumber(winningsBonus, getCurrency())}</RowValue>
            </SummaryRow>
          ) : null}
          <SummaryRow>
            <RowLabel>{__('Winnings')}</RowLabel>
            <RowValue
              $color={amount > 0 ? theme.History.betslip.summary.greenRow : theme.History.betslip.summary.redRow}
            >
              {Formatter.formatNumber(amount, getCurrency())}
            </RowValue>
          </SummaryRow>
        </>
      );
    }
    return null;
  }, [promotionTitle, status, winningsAmount, winningsBonus]);

  const runningSummary = useMemo(() => {
    if (status === BetslipStatusTypes.RUNNING) {
      return (
        <RunningSummaryWrapper>
          {possibleBonus > 0 ? (
            <SummaryRow>
              <RowLabel>
                {__('Winnings')} {promotionTitle}
              </RowLabel>
              <RowValue>{Formatter.formatNumber(possibleBonus, getCurrency())}</RowValue>
            </SummaryRow>
          ) : null}
          <SummaryRow>
            <RowLabel>{__('Potential winnings')}</RowLabel>
            <RowValue $color={theme.History.betslip.summary.yellowRow}>
              {Formatter.formatNumber(possibleWinnings + (possibleBonus || 0), getCurrency())}
            </RowValue>
          </SummaryRow>
          {winningsAmount > 0 ? (
            <>
              <SummaryRow>
                <RowLabel $uppercase>{__('already received')}</RowLabel>
                <RowValue>{Formatter.formatNumber(winningsAmount + (winningsBonus || 0), getCurrency())}</RowValue>
              </SummaryRow>
              {winningsBonus > 0 ? (
                <DetailsRow>
                  <RowValueDetails>
                    ({__('including %1$s:', promotionTitle)}
                    &nbsp;
                    <b>{Formatter.formatNumber(winningsBonus, getCurrency())}</b>)
                  </RowValueDetails>
                </DetailsRow>
              ) : null}
            </>
          ) : null}
        </RunningSummaryWrapper>
      );
    }
    return null;
  }, [possibleBonus, possibleWinnings, promotionTitle, status, winningsAmount, winningsBonus]);

  const compactSummary = useMemo(() => {
    let amount = 0;
    let label = null;
    let color = null;

    if (status === BetslipStatusTypes.SETTLED || status === BetslipStatusTypes.CASHOUT) {
      label = __('Winnings');
      amount = winningsAmount + (winningsBonus || 0);

      if (amount > 0) {
        color = theme.History.betslip.summary.greenRow;
      }
    } else if (status === BetslipStatusTypes.RUNNING) {
      label = __('Potential winnings');
      amount = possibleWinnings + (possibleBonus || 0);
      color = theme.History.betslip.summary.yellowRow;
    }

    return label ? (
      <CompactSummaryContent>
        <CompactSummaryLabel>{label}</CompactSummaryLabel>
        <CompactSummaryValue $color={color}>{Formatter.formatNumber(amount, getCurrency())}</CompactSummaryValue>
      </CompactSummaryContent>
    ) : null;
  }, [status, winningsAmount, winningsBonus, possibleWinnings, possibleBonus]);

  if (isCompact) {
    return (
      <CompactSummary>
        <CompactSummaryContent>
          <CompactSummaryLabel>
            {freebet ? (
              <>
                {__('Freebet Wager')}
                <FreeBetsImg src={FreebetsIcon} />
              </>
            ) : (
              __('Wager')
            )}
          </CompactSummaryLabel>
          <CompactSummaryValue>{Formatter.formatNumber(betAmount, getCurrency())}</CompactSummaryValue>
        </CompactSummaryContent>
        {compactSummary}
      </CompactSummary>
    );
  }

  return (
    <Wrapper className={className}>
      {defaultSummary}
      {!!settledAndCashoutSummary || !!runningSummary ? <LineSeparator /> : null}
      {settledAndCashoutSummary}
      {runningSummary}
    </Wrapper>
  );
};
export default HistoryItemBetslipSummary;
